// src/utils/photoRuns.js — v0.320.0 (2026-11-04)
// ---------------------------------------------------------------------------
// Photos sent together read as ONE message.
//
// Brett, 2026-11-04 (on the first drag of six pot photos into a client chat):
// "Looks like you are sending a message for every photo. They should be
// grouped." Each photo is still its own message on the server, so everything
// that reads a conversation (the client's report, the receiving system, Remove,
// Add to item photos) keeps working photo by photo. The grouping is how a
// thread is DRAWN: a photo with no words of its own joins the message just
// before it when that one is a photo too, from the same person, in the same
// chat, within RUN_GAP_MS. Words start a new message, so a reply in between
// always splits two runs. A removed message never joins one (its photo is gone
// on the client's side and struck on the office's), and a held one only joins
// another held one.
//
//   photoRuns(list) -> [{ lead, members }]   lead = members[0]

export const RUN_GAP_MS = 2 * 60 * 1000;

const removedOf = (c) => !!(c && (c.removedAt || c.removed));
const hasPhoto = (c) => !!(c && c.photo && (c.photo.thumbnailUrl || c.photo.displayUrl || c.photo.url));
const wordless = (c) => !c.body || c.body === '(photo)';
const same = (a, b) => (a == null ? null : a) === (b == null ? null : b);

function sameSender(a, b) {
  return a.authorRole === b.authorRole
    && same(a.authorName, b.authorName)
    && same(a.viaShareId, b.viaShareId)
    && (a.chat === 'trade' ? 'trade' : 'client') === (b.chat === 'trade' ? 'trade' : 'client')
    && same(a.tradeShareId, b.tradeShareId);
}

/** True when `c` continues the photo message `prev`. */
export function joinsRun(prev, c) {
  if (!prev || !c) return false;
  if (!hasPhoto(prev) || !hasPhoto(c) || !wordless(c)) return false;
  if (removedOf(prev) || removedOf(c)) return false;
  if (!!prev.heldAt !== !!c.heldAt) return false;
  if (!sameSender(prev, c)) return false;
  const gap = Date.parse(c.createdAt) - Date.parse(prev.createdAt);
  return Number.isFinite(gap) && gap >= 0 && gap <= RUN_GAP_MS;
}

export function photoRuns(list) {
  const out = [];
  for (const c of Array.isArray(list) ? list : []) {
    const last = out[out.length - 1];
    if (last && joinsRun(last.members[last.members.length - 1], c)) last.members.push(c);
    else out.push({ lead: c, members: [c] });
  }
  return out;
}

/** Everyone who has seen any message of a run, each once (the newest sighting). */
export function runSeenBy(members) {
  const byName = new Map();
  for (const m of members) {
    for (const p of (Array.isArray(m.seenBy) ? m.seenBy : [])) {
      const had = byName.get(p.name);
      if (!had || String(p.at || '') > String(had.at || '')) byName.set(p.name, p);
    }
  }
  return [...byName.values()].sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')));
}
